import React, { useState, useEffect, useCallback, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // Add useLocation
import { useAuth } from "./AuthContext"; // Import useAuth from AuthContext
import styled from "styled-components";

// Styled components (unchanged)
const MenuBar = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ddd;
  font-family: "Arial", sans-serif;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const HamburgerIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  margin-right: 15px;

  span {
    display: block;
    height: 3px;
    background-color: #555;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    margin-right: 10px;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  padding: 5px 10px;
  border-radius: 5px;
  margin-right: 15px;

  @media (max-width: 768px) {
    margin-right: 10px;
  }
`;

const ImageHeader = styled.div`
  font-size: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #333;
  text-align: center;

  span {
    font-size: 0.6rem;
    color: #777;
    display: block;
    margin-top: -3px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 15px;

  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

const DropdownSelect = styled.select`
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  background-color: #fff;
  color: #333;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    margin-left: 0;
    margin-top: 10px;
  }
`;

const UserGreeting = styled.div`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  margin-right: 15px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-right: 10px;
  }
`;

const LogoutButton = styled.button`
  padding: 5px 10px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: #d32f2f;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 5px 8px;
  }
`;

const TopLeftMenu = ({ userEmail, userDepartment }) => {
  // Ensure userDepartment is a string (handle array case)
  const department = Array.isArray(userDepartment) ? userDepartment[0] : userDepartment;

  const [selectedOption, setSelectedOption] = useState(department || "");
  const navigate = useNavigate();
  const location = useLocation(); // Add useLocation to track URL changes
  const { logout } = useAuth(); // Use the logout function from AuthContext

  // Debugging: Log userDepartment and selectedOption
  console.log("userDepartment:", userDepartment);
  console.log("department (processed):", department);
  console.log("selectedOption:", selectedOption);
  console.log("Current URL:", location.pathname); // Log current URL

  // Sync selectedOption with the current URL
  useEffect(() => {
    const path = location.pathname;
    let newSelectedOption = "";

    switch (path) {
      case "/sales":
        newSelectedOption = "Sales";
        break;
      case "/pm":
        newSelectedOption = "PM";
        break;
      case "/bdm":
        newSelectedOption = "BDM";
        break;
      case "/user_management": // Add this case for User Management
        newSelectedOption = "User Management";
        break;
      case "/user_user_management":
        newSelectedOption = "Admin";
        break;
      case "/pm-admin":
        newSelectedOption = "PM Admin";
        break;
      case "/sales-admin":
        newSelectedOption = "Sales Admin";
        break;
      case "/bdm-admin":
        newSelectedOption = "BDM Admin";
        break;
      default:
        newSelectedOption = department || "";
        break;
    }

    if (newSelectedOption !== selectedOption) {
      setSelectedOption(newSelectedOption);
    }
  }, [location.pathname, department]);

  // Handle dropdown change
  const handleDropdownChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedOption(value);

    // Navigate immediately when the dropdown changes
    switch (value) {
      case "Sales":
        navigate("/sales");
        break;
      case "PM":
        navigate("/pm");
        break;
      case "BDM":
        navigate("/bdm");
        break;
      case "Admin":
        navigate("/user_user_management");
        break;
      case "PM Admin":
        navigate("/pm-admin");
        break;
      case "Sales Admin":
        navigate("/sales-admin");
        break;
      case "BDM Admin":
        navigate("/bdm-admin");
        break;
      case "User Management": // Add this case for User Management
        navigate("/user_management");
        break;
      default:
        console.error(`Invalid department: ${value}`);
        break;
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    logout(); // Use the logout function from AuthContext
    navigate("/login");
  }, [logout, navigate]);

  const renderDropdownOptions = useCallback(() => {
    // Ensure department is a string before calling trim()
    const normalizedDepartment =
      typeof department === "string" ? department.trim().toLowerCase() : "";

    console.log("normalizedDepartment:", normalizedDepartment); // Debugging

    switch (normalizedDepartment) {
      case "admin":
        return (
          <>
            <option value="Sales">Sales</option>
            <option value="PM">PM</option>
            <option value="BDM">BDM</option>
            <option value="PM Admin">PM Admin</option>
            <option value="Sales Admin">Sales Admin</option>
            <option value="BDM Admin">BDM Admin</option>
            <option value="User Management">User Management</option>
          </>
        );
      case "sales":
        return (
          <>
            <option value="Sales">Sales</option>
          </>
        );
      case "pm":
        return (
          <>
            <option value="PM">PM</option>
          </>
        );
      case "bdm":
        return (
          <>
            <option value="BDM">BDM</option>
          </>
        );
      case "user management":
        return (
          <>
            <option value="User Management">User Management</option>
          </>
        );
      default:
        console.error(`Invalid department: ${department}`);
        return null;
    }
  }, [department]);

  return (
    <MenuBar>
      <LeftSection>
        <HamburgerIcon>
          <span></span>
          <span></span>
          <span></span>
        </HamburgerIcon>

        <HeaderWrapper>
          <ImageHeader>
            I M A G E
          </ImageHeader>
        </HeaderWrapper>

        <DropdownContainer>
          <DropdownSelect value={selectedOption} onChange={handleDropdownChange}>
            {renderDropdownOptions()}
          </DropdownSelect>
        </DropdownContainer>
      </LeftSection>

      <RightSection>
        {userEmail && <UserGreeting>Hello, <span>{userEmail}</span></UserGreeting>}
        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
      </RightSection>
    </MenuBar>
  );
};

export default memo(TopLeftMenu);